import { Button, Flex, Image } from '@adobe/react-spectrum';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import { BadgePopover, HeaderContainer, WithSkeleton } from '@exchange-frontends/components';
import { useMediaQuery } from '@exchange-frontends/custom-hooks';
import { handleHeaderForLaunch } from '@exchange-frontends/utils';

import { useStore } from '../../store';
import getAppIcon from '../../utils/GetAppIcon';
import HeaderButtons from './header-buttons/HeaderButtons';
import PublisherLink from './sidebar/PublisherLink';

/**
 * styled publisher name
 * @memberof Header
 * @type {styled-component}
 */
const PublisherName = styled.span`
  margin: 0 0 0 var(--spectrum-global-dimension-size-125);
  font-size: 12px;
  color: rgba(116, 116, 116, 1);
`;

const ImageContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  & > div {
    width: 100%; /* had to do this because of Chrome */
  }
  & img {
    border-radius: 4px;
  }
`;

const Title = styled.h3`
  margin: 0 var(--spectrum-global-dimension-size-175) 0 var(--spectrum-global-dimension-size-125);
  line-height: 1.2em;
  font-size: ${({ $isMobileSized }) => ($isMobileSized ? '1em' : 'auto')};
`;

/**
 * Sticky header for Experience Cloud Details
 */
const Header = () => {
  const isMobileSized = useMediaQuery('(max-width: 500px)');
  const detailScreen = useStore((state) => state.detailScreen);
  const detailScreenLoading = useStore((state) => state.detailScreenLoading);

  const isSolution = detailScreen?.appType === 'SLN';
  const PublisherWrapper = isSolution ? PublisherLink : Fragment;

  return (
    // Don't even dream of removing data-newrelicid="app-details-header-info" this will cause a CSO
    <HeaderContainer
      data-testid="app-details-header"
      onClickCapture={handleHeaderForLaunch}
      data-newrelicid="app-details-header-info"
    >
      <Flex
        wrap={isMobileSized ? 'nowrap' : 'auto'}
        data-testid="app-details-header-flex"
        alignItems="center"
        width="100%"
        justifyContent="space-between"
      >
        <Flex alignItems="center" marginStart="20px" justifyContent="space-between">
          <WithSkeleton
            shouldRender={!detailScreenLoading && !!detailScreen?.media?.icon}
            enableAnimation={detailScreenLoading}
            borderRadius="4px"
            width="40px"
            height="40px"
          >
            {detailScreen?.media?.icon && (
              <ImageContainer>
                <Image src={getAppIcon(detailScreen.media.icon)} alt="" objectFit="contain" />
              </ImageContainer>
            )}
          </WithSkeleton>
          <Flex direction="column">
            <Flex alignItems="center">
              <Title $isMobileSized={isMobileSized}>
                <WithSkeleton shouldRender={!detailScreenLoading} width={200}>
                  <span
                    style={{
                      lineHeight: '1em',
                      color: 'var(--spectrum-global-color-gray-900)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {detailScreen?.name}
                  </span>
                </WithSkeleton>
              </Title>
            </Flex>
            <PublisherName marginStart="size-125">
              <WithSkeleton shouldRender={!detailScreenLoading} width={200}>
                <PublisherWrapper>{detailScreen?.publisher?.name}</PublisherWrapper>
              </WithSkeleton>
            </PublisherName>
          </Flex>
          {!detailScreenLoading && detailScreen?.isVerified && (
            <Flex width="72px" marginEnd="size-100">
              <BadgePopover badgeType="isVerified" />
            </Flex>
          )}
        </Flex>
        {detailScreen?.pricingInfo && <HeaderButtons />}
        {detailScreen?.appType === 'integrations' && detailScreen?.trialUrl && (
          <Button
            isDisabled={detailScreenLoading}
            variant="cta"
            marginEnd="30px"
            onPress={() => {
              window.open(detailScreen.trialUrl);
            }}
          >
            Connect
          </Button>
        )}
      </Flex>
    </HeaderContainer>
  );
};

export default Header;
